import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Copy, Plus, Trash2, Pencil, Check, X, ChevronLeft, ChevronRight, KeyRound, AlertTriangle, CheckCircle2, Link2 } from "lucide-react";
import { api, API, errMsg, fmtDateTime } from "@/lib/api";
import { PageTitle } from "@/components/admin/AdminLayout";

const BASE = API.replace(/\/api$/, "");
const PRESETS = ["Airbnb", "Booking.com", "Abritel / Vrbo", "Gîtes de France", "Google Agenda", "Autre"];
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const FEED_COLORS = ["bg-sky-500", "bg-rose-500", "bg-violet-500", "bg-teal-500", "bg-orange-500", "bg-lime-600"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtD = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "");
const nights = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);

async function copy(text) {
  try { await navigator.clipboard.writeText(text); toast.success("Adresse copiée"); }
  catch { window.prompt("Copiez l'adresse :", text); }
}

function UrlBox({ url, testId, onRegenerate, help }) {
  return (
    <div>
      <div className="flex items-stretch gap-2">
        <input readOnly value={url} onFocus={(e) => e.target.select()} className="input font-mono text-xs flex-1 min-w-0" data-testid={testId} />
        <button type="button" onClick={() => copy(url)} className="btn-secondary px-3 min-h-0" title="Copier" data-testid={`${testId}-copy`}><Copy size={15} /></button>
        {onRegenerate && <button type="button" onClick={onRegenerate} className="btn-ghost px-3 min-h-0" title="Générer une nouvelle adresse" data-testid={`${testId}-regen`}><KeyRound size={15} /></button>}
      </div>
      {help && <p className="text-xs text-charcoal/50 mt-1.5">{help}</p>}
    </div>
  );
}

function StatusBadge({ feed }) {
  if (!feed.active) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-sand/70 text-charcoal/60">Désactivé</span>;
  if (feed.last_status === "ok") return <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"><CheckCircle2 size={12} /> Synchronisé</span>;
  if (feed.last_status === "error") return <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700"><AlertTriangle size={12} /> Erreur</span>;
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-sand/70 text-charcoal/60">En attente</span>;
}

function FeedForm({ initial, onSubmit, onCancel, busy }) {
  const [preset, setPreset] = useState(initial ? (PRESETS.includes(initial.name) ? initial.name : "Autre") : "Airbnb");
  const [name, setName] = useState(initial?.name || "");
  const [url, setUrl] = useState(initial?.url || "");
  const finalName = preset === "Autre" ? name : preset;
  return (
    <form className="grid sm:grid-cols-[180px_1fr_auto] gap-3 items-end" onSubmit={(e) => { e.preventDefault(); onSubmit({ name: finalName.trim(), url: url.trim() }); }} data-testid="feed-form">
      <div>
        <label className="label">Plateforme</label>
        <select className="input" value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="feed-preset">{PRESETS.map((p) => <option key={p}>{p}</option>)}</select>
        {preset === "Autre" && <input className="input mt-2" placeholder="Nom du calendrier" value={name} onChange={(e) => setName(e.target.value)} required data-testid="feed-name" />}
      </div>
      <div>
        <label className="label">Adresse iCal d’import (.ics)</label>
        <input className="input font-mono text-xs" type="text" inputMode="url" placeholder="https://www.airbnb.fr/calendar/ical/…ics" value={url} onChange={(e) => setUrl(e.target.value)} required data-testid="feed-url" />
      </div>
      <div className="flex gap-2">
        <button disabled={busy || !finalName.trim() || !url.trim()} className="btn-primary min-h-[44px]" data-testid="feed-submit">{busy ? <RefreshCw size={15} className="animate-spin" /> : <Check size={15} />} {initial ? "Enregistrer" : "Ajouter"}</button>
        {onCancel && <button type="button" onClick={onCancel} className="btn-ghost min-h-[44px] px-3" aria-label="Annuler"><X size={15} /></button>}
      </div>
    </form>
  );
}

function FeedRow({ feed, color, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const sync = () => run(async () => { const r = await api.post(`/admin/calendar/feeds/${feed.id}/sync`); if (r.data.status === "error") throw new Error(r.data.error); toast.success(`${r.data.events} période(s) importée(s)`); });
  const toggle = () => run(() => api.put(`/admin/calendar/feeds/${feed.id}`, { active: !feed.active }), feed.active ? "Flux désactivé : ses dates ne bloquent plus le calendrier" : "Flux réactivé");
  const remove = () => { if (window.confirm(`Supprimer le flux « ${feed.name} » et les dates importées depuis celui-ci ?`)) run(() => api.delete(`/admin/calendar/feeds/${feed.id}`), "Flux supprimé"); };
  const regen = () => { if (window.confirm("Générer une nouvelle adresse d'export ? L'ancienne cessera de fonctionner : il faudra la remplacer sur la plateforme.")) run(() => api.post(`/admin/calendar/feeds/${feed.id}/export-token`), "Nouvelle adresse générée"); };
  const save = (body) => run(async () => { const r = await api.put(`/admin/calendar/feeds/${feed.id}`, body); setEdit(false); if (r.data.sync?.status === "error") toast.error(r.data.sync.error); }, "Flux mis à jour");
  return (
    <li className="py-4 border-t border-sand/70 first:border-t-0" data-testid={`feed-row-${feed.id}`}>
      {edit ? <FeedForm initial={feed} onSubmit={save} onCancel={() => setEdit(false)} busy={busy} /> : (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className={`w-2.5 h-2.5 rounded-full ${color}`} aria-hidden />
            <p className="font-medium text-forest">{feed.name}</p>
            <StatusBadge feed={feed} />
            <span className="text-xs text-charcoal/50">{feed.events_count || 0} période(s){feed.last_sync_at ? ` · ${fmtDateTime(feed.last_sync_at)}` : ""}</span>
            <div className="ml-auto flex gap-1">
              <button onClick={sync} disabled={busy || !feed.active} className="btn-ghost px-3 py-2 min-h-0 text-xs" data-testid={`feed-sync-${feed.id}`}><RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Synchroniser</button>
              <button onClick={toggle} disabled={busy} className="btn-ghost px-3 py-2 min-h-0 text-xs" data-testid={`feed-toggle-${feed.id}`}>{feed.active ? "Désactiver" : "Activer"}</button>
              <button onClick={() => setEdit(true)} className="btn-ghost px-2.5 py-2 min-h-0" aria-label="Modifier" data-testid={`feed-edit-${feed.id}`}><Pencil size={13} /></button>
              <button onClick={remove} disabled={busy} className="btn-ghost px-2.5 py-2 min-h-0 text-red-600" aria-label="Supprimer" data-testid={`feed-delete-${feed.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
          <p className="text-xs font-mono text-charcoal/50 mt-1 break-all">{feed.url}</p>
          {feed.last_status === "error" && feed.last_error && <p className="text-xs text-red-700 mt-1" data-testid={`feed-error-${feed.id}`}>{feed.last_error} — les dates déjà importées sont conservées.</p>}
          <div className="mt-3 rounded-xl bg-ecru/60 p-3">
            <p className="text-xs font-semibold text-forest/80 mb-1.5 inline-flex items-center gap-1.5"><Link2 size={13} /> Adresse d’export à coller dans {feed.name}</p>
            <UrlBox url={`${BASE}/api/ical/${feed.export_token}.ics`} testId={`feed-export-${feed.id}`} onRegenerate={regen}
              help={`Contient vos blocages directs et les réservations des autres plateformes, sans celles de ${feed.name}.`} />
          </div>
        </>
      )}
    </li>
  );
}

function BlockForm({ apartmentId, reasons, initial, onDone, onCancel, prefill }) {
  const [f, setF] = useState(initial || { start: "", end: "", reason: reasons[0] || "Réservation directe", note: "" });
  useEffect(() => { if (prefill) setF((x) => ({ ...x, start: prefill.start || "", end: prefill.end || "" })); }, [prefill]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.end <= f.start) return toast.error("La date de départ doit être après la date d'arrivée");
    setBusy(true);
    try {
      const body = { apartment_id: apartmentId, start: f.start, end: f.end, reason: f.reason, note: f.note || "" };
      const r = initial ? await api.put(`/admin/calendar/blocks/${initial.id}`, body) : await api.post("/admin/calendar/blocks", body);
      if (r.data.conflicts?.length) toast.warning(`Attention : ces dates chevauchent ${r.data.conflicts.length} période(s) déjà occupée(s)`);
      else toast.success(initial ? "Blocage modifié" : "Dates bloquées");
      if (!initial) setF({ start: "", end: "", reason: f.reason, note: "" });
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-[150px_150px_190px_1fr_auto] gap-3 items-end" data-testid={initial ? `block-edit-form-${initial.id}` : "block-form"}>
      <div><label className="label">Arrivée</label><input type="date" className="input" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} required data-testid="block-start" /></div>
      <div><label className="label">Départ</label><input type="date" className="input" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} required data-testid="block-end" /></div>
      <div><label className="label">Motif</label><select className="input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="block-reason">{reasons.map((r) => <option key={r}>{r}</option>)}</select></div>
      <div><label className="label">Note interne (facultatif)</label><input className="input" value={f.note} maxLength={500} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="ex. famille Martin, réservation par téléphone" data-testid="block-note" /></div>
      <div className="flex gap-2">
        <button disabled={busy} className="btn-primary min-h-[44px]" data-testid="block-submit">{initial ? <Check size={15} /> : <Plus size={15} />} {initial ? "Enregistrer" : "Bloquer"}</button>
        {onCancel && <button type="button" onClick={onCancel} className="btn-ghost min-h-[44px] px-3" aria-label="Annuler"><X size={15} /></button>}
      </div>
    </form>
  );
}

function MonthGrid({ year, month, dayInfo, pick, onPick }) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // semaine commençant le lundi
  const days = new Date(year, month + 1, 0).getDate();
  const today = iso(new Date());
  const cells = [...Array(offset).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <div>
      <p className="font-serif text-lg text-forest capitalize mb-2">{MONTHS[month]} {year}</p>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-charcoal/40 mb-1">{["L", "M", "M", "J", "V", "S", "D"].map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const key = iso(new Date(year, month, d));
          const info = dayInfo[key];
          return (
            <button type="button" key={i} title={info ? info.labels.join(" · ") : "Libre — cliquez pour sélectionner"} data-testid={`cal-day-${key}`} data-busy={info ? "1" : "0"}
              onClick={() => onPick(key)}
              className={`relative h-9 rounded-md text-xs flex items-center justify-center ${info ? `${info.color} text-white font-medium` : "bg-white border border-sand/70 text-charcoal/70 hover:border-forest"} ${key === today ? "ring-2 ring-gold ring-offset-1" : ""} ${info?.multi ? "outline outline-2 outline-red-500" : ""} ${pick?.start && (key === pick.start || (pick.end && key >= pick.start && key < pick.end)) ? "!ring-2 !ring-forest ring-offset-1" : ""}`}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApartmentCalendar({ data, reasons, onChanged }) {
  const { apartment, feeds, events } = data;
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editBlock, setEditBlock] = useState(null);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [pick, setPick] = useState(null);
  // blocage rapide : 1er clic = arrivée, 2e clic = départ -> formulaire prérempli
  const onPick = (key) => setPick((p) => (!p?.start || p.end || key <= p.start ? { start: key, end: "" } : { start: p.start, end: key }));
  // couleur stable par flux (ne change pas si un autre flux est supprimé)
  const colorOf = useMemo(() => Object.fromEntries(feeds.map((f) => [f.id, FEED_COLORS[[...f.id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % FEED_COLORS.length]])), [feeds]);
  const dayInfo = useMemo(() => {
    const map = {};
    for (const e of events) {
      const color = e.source === "manual" ? "bg-amber-500" : e.source === "booking" ? "bg-emerald-600" : colorOf[e.feed_id] || "bg-sky-500";
      const label = e.source === "feed" ? e.feed_name : `${e.reason}${e.note ? ` (${e.note})` : ""}`;
      for (let d = new Date(`${e.start}T12:00:00`); iso(d) < e.end; d.setDate(d.getDate() + 1)) {
        const k = iso(d);
        if (map[k]) { map[k].labels.push(label); map[k].multi = map[k].labels.length > 1 && !map[k].labels.every((l) => l === label); }
        else map[k] = { color, labels: [label], multi: false };
      }
    }
    return map;
  }, [events, colorOf]);
  const months = [0, 1, 2].map((i) => { const d = new Date(cursor.y, cursor.m + i, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const shift = (n) => setCursor((c) => { const d = new Date(c.y, c.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const blocks = events.filter((e) => e.source === "manual");
  const addFeed = async (body) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/calendar/feeds", { apartment_id: apartment.id, ...body });
      if (r.data.sync?.status === "error") toast.error(`Flux ajouté, mais la synchronisation a échoué : ${r.data.sync.error}`);
      else toast.success(`Flux ajouté : ${r.data.sync?.events ?? 0} période(s) importée(s)`);
      setAdding(false); onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const regenApt = async () => {
    if (!window.confirm("Générer une nouvelle adresse d'export ? L'ancienne cessera de fonctionner.")) return;
    try { await api.post(`/admin/calendar/apartments/${apartment.id}/export-token`); toast.success("Nouvelle adresse générée"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delBlock = async (b) => {
    if (!window.confirm(`Débloquer du ${fmtD(b.start)} au ${fmtD(b.end)} ?`)) return;
    try { await api.delete(`/admin/calendar/blocks/${b.id}`); toast.success("Dates débloquées"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid={`calendar-apartment-${apartment.slug}`}>
      <section className="card p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="font-serif text-2xl text-forest">Disponibilités</h2>
          <div className="flex gap-1">
            <button onClick={() => shift(-1)} className="btn-ghost px-2.5 py-2 min-h-0" aria-label="Mois précédent" data-testid="cal-prev"><ChevronLeft size={16} /></button>
            <button onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }} className="btn-ghost px-3 py-2 min-h-0 text-xs">Aujourd’hui</button>
            <button onClick={() => shift(1)} className="btn-ghost px-2.5 py-2 min-h-0" aria-label="Mois suivant" data-testid="cal-next"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">{months.map((x) => <MonthGrid key={`${x.y}-${x.m}`} year={x.y} month={x.m} dayInfo={dayInfo} pick={pick} onPick={onPick} />)}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 mt-5 text-xs text-charcoal/70">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-sand" /> Libre</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-500" /> Blocage direct</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-600" /> Réservation du site</span>
          {feeds.map((f) => <span key={f.id} className="inline-flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${colorOf[f.id]}`} /> {f.name}</span>)}
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded outline outline-2 outline-red-500" /> Chevauchement</span>
        </div>
      </section>

      <section className="card p-5 sm:p-6">
        <h2 className="font-serif text-2xl text-forest">Bloquer des dates</h2>
        <p className="text-sm text-charcoal/60 mt-1 mb-4">Réservation directe, travaux, usage personnel… Ces dates sont envoyées à toutes les plateformes via les adresses d’export. La date de départ est libre pour une nouvelle arrivée.</p>
        <p className="text-xs text-charcoal/50 mb-3">Astuce : cliquez sur le jour d’arrivée puis sur le jour de départ dans le calendrier ci-dessus pour préremplir les dates.</p>
        <BlockForm apartmentId={apartment.id} reasons={reasons} prefill={pick} onDone={() => { setPick(null); onChanged(); }} />
        {blocks.length > 0 && (
          <ul className="mt-5 divide-y divide-sand/70" data-testid="block-list">
            {blocks.map((b) => (
              <li key={b.id} className="py-3" data-testid={`block-row-${b.id}`}>
                {editBlock === b.id ? <BlockForm apartmentId={apartment.id} reasons={reasons} initial={b} onDone={() => { setEditBlock(null); onChanged(); }} onCancel={() => setEditBlock(null)} /> : (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" aria-hidden />
                    <p className="text-sm text-forest font-medium">{fmtD(b.start)} → {fmtD(b.end)} <span className="text-charcoal/50 font-normal">· {nights(b.start, b.end)} nuit(s)</span></p>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">{b.reason}</span>
                    {b.note && <span className="text-xs text-charcoal/60">{b.note}</span>}
                    <div className="ml-auto flex gap-1">
                      <button onClick={() => setEditBlock(b.id)} className="btn-ghost px-2.5 py-2 min-h-0" aria-label="Modifier" data-testid={`block-edit-${b.id}`}><Pencil size={13} /></button>
                      <button onClick={() => delBlock(b)} className="btn-ghost px-2.5 py-2 min-h-0 text-red-600" aria-label="Débloquer" data-testid={`block-delete-${b.id}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-serif text-2xl text-forest">Calendriers importés</h2>
            <p className="text-sm text-charcoal/60 mt-1">Collez l’adresse iCal (.ics) fournie par chaque plateforme. Synchronisation automatique toutes les {data.interval || 30} minutes.</p>
          </div>
          {!adding && <button onClick={() => setAdding(true)} className="btn-secondary" data-testid="feed-add-btn"><Plus size={15} /> Ajouter un calendrier</button>}
        </div>
        {adding && <div className="mt-4 rounded-xl border border-sand p-4"><FeedForm onSubmit={addFeed} onCancel={() => setAdding(false)} busy={busy} /></div>}
        {feeds.length ? <ul className="mt-2" data-testid="feed-list">{feeds.map((f) => <FeedRow key={f.id} feed={f} color={colorOf[f.id]} onChanged={onChanged} />)}</ul>
          : !adding && <p className="text-sm text-charcoal/50 mt-4">Aucun calendrier importé pour cet appartement.</p>}
      </section>

      <section className="card p-5 sm:p-6">
        <h2 className="font-serif text-2xl text-forest">Export complet</h2>
        <p className="text-sm text-charcoal/60 mt-1 mb-4">Toutes les dates occupées (blocages directs + toutes les plateformes). À utiliser pour une plateforme qui n’est pas encore importée ci-dessus, ou pour votre agenda personnel. Pour une plateforme déjà importée, utilisez plutôt son adresse d’export dédiée (sinon ses propres réservations lui reviendraient en double).</p>
        <UrlBox url={`${BASE}${data.export_path}`} testId={`apt-export-${apartment.slug}`} onRegenerate={regenApt} />
      </section>
    </div>
  );
}

export default function CalendarsAdmin() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(() => api.get("/admin/calendar/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/calendar/sync");
      if (!r.data.feeds) toast.info("Aucun calendrier à synchroniser");
      else if (r.data.errors) toast.warning(`${r.data.ok} calendrier(s) synchronisé(s), ${r.data.errors} en erreur`);
      else toast.success(`${r.data.ok} calendrier(s) synchronisé(s)`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); }
  };
  const apts = data?.apartments || [];
  const current = apts[tab];
  return (
    <div>
      <PageTitle title="Calendriers" text="Import des calendriers Airbnb, Booking, Abritel…, blocages manuels et adresses d'export .ics par appartement.">
        <button onClick={syncAll} disabled={syncing} className="btn-primary" data-testid="calendar-sync-all"><RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Synchronisation…" : "Synchroniser maintenant"}</button>
      </PageTitle>
      {data && (
        <p className="text-xs text-charcoal/60 mb-5" data-testid="calendar-sync-status">
          Synchronisation automatique {data.sync.scheduler_active ? `active, toutes les ${data.sync.interval_minutes} min` : "désactivée"}
          {data.sync.last_run_at ? ` · dernière exécution : ${fmtDateTime(data.sync.last_run_at)} (${data.sync.last_trigger === "manual" ? "manuelle" : "automatique"})` : " · pas encore exécutée"}
        </p>
      )}
      <div className="flex flex-wrap gap-2 mb-6" role="tablist">
        {apts.map((a, i) => (
          <button key={a.apartment.id} role="tab" aria-selected={tab === i} onClick={() => setTab(i)} data-testid={`calendar-tab-${a.apartment.slug}`}
            className={`px-4 py-2 rounded-full text-sm border transition ${tab === i ? "bg-forest text-ecru border-forest" : "bg-white border-sand text-forest hover:border-forest"}`}>
            {a.apartment.name}
            {a.feeds.some((f) => f.active && f.last_status === "error") && <AlertTriangle size={13} className="inline ml-1.5 text-red-500" />}
          </button>
        ))}
      </div>
      {!data ? <p className="text-sm text-charcoal/50">Chargement…</p>
        : current ? <ApartmentCalendar key={current.apartment.id} data={{ ...current, interval: data.sync.interval_minutes }} reasons={data.reasons} onChanged={load} />
          : <p className="text-sm text-charcoal/50">Aucun appartement.</p>}
    </div>
  );
}
